import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import api from '../../services/api'
import type { Memory } from '../../types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'

interface OrganizationMemoriesProps {
  organizationId: string
}

export function OrganizationMemories({ organizationId }: OrganizationMemoriesProps) {
  const [memories, setMemories] = useState<Memory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadMemories()
  }, [organizationId])

  const loadMemories = async () => {
    try {
      setLoading(true)
      // Filter memories by organization_id
      const allMemories = await api.getMemories({ limit: 100 })
      const filtered = allMemories.filter(m => m.organization_id === organizationId)
      setMemories(filtered)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load memories')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading memories...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
      </div>
    )
  }

  if (memories.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-8">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No memories linked to this organization yet</p>
          <p className="text-sm text-muted-foreground">
            Create memories and link them to this organization from the Memories page
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{memories.length} Memories</h3>
      </div>
      <div className="grid gap-4">
        {memories.map((memory) => (
          <Card key={memory.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base line-clamp-2">
                    {memory.content.substring(0, 100)}...
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {new Date(memory.created_at).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{memory.category}</Badge>
                  {memory.department_id && <Badge variant="outline">Dept</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {memory.tags?.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
